import { useCallback, useMemo, useState } from 'react';
import { useTracking } from 'react-tracking';

import { LoadingState } from '../LoadingState';
import { ErrorState } from '../ErrorState';
import { EmptyState } from '../EmptyState';
import { PopulatedState } from '../PopulatedState';

import { usePortfolioPerformanceTimeseries } from '@/hooks/portfolio/usePortfolioPerformanceTimeseries';
import { useBenchmarkTimeseries, useUtcOffsetPeriodMap } from '../../hooks';
import { getBenchmarkMetricLabel } from '../../utils';
import type { Period } from '../../types';
import type { BenchmarkType } from '../../hooks';

type Props = {
  portfolioId: string;
  benchmarkType: BenchmarkType;
  benchmark: string;
};

export const DemoPortfolioPerformanceChart = ({
  portfolioId,
  benchmark,
  benchmarkType,
}: Props) => {
  const { trackEvent } = useTracking({
    type: 'track',
    modifier: 'performanceChart',
  });
  const [benchmarkOption, setBenchmarkOption] = useState<string>(benchmark);
  const [period, setPeriod] = useState<Period>('1Y');

  const periodMap = useUtcOffsetPeriodMap();

  const portfolioTimeseries = usePortfolioPerformanceTimeseries({
    portfolioId,
    startTimestamp: periodMap[period],
    endTimestamp: periodMap.NOW,
  });

  const benchmarkTimeseries = useBenchmarkTimeseries({
    benchmark: benchmarkOption,
    benchmarkType,
    periodStart: periodMap[period],
    periodEnd: periodMap.NOW,
  });

  const handleBenchmarkOptionChange = useCallback(
    (benchmark: string, benchmarkType: BenchmarkType) => {
      trackEvent({
        action: 'changeBenchmark',
        benchmark,
        benchmarkType,
      });
      setBenchmarkOption(benchmark);
    },
    [trackEvent, setBenchmarkOption]
  );

  const isEmptyData = useMemo(() => {
    return (
      portfolioTimeseries?.data === undefined ||
      Object.values(portfolioTimeseries.data).length === 0
    );
  }, [portfolioTimeseries.data]);

  if (
    portfolioTimeseries.status === 'pending' ||
    benchmarkTimeseries.status === 'pending'
  ) {
    return (
      <LoadingState
        period={period}
        benchmarkMetricLabel={getBenchmarkMetricLabel(benchmarkType, benchmark)}
      />
    );
  }

  if (
    portfolioTimeseries.status === 'error' ||
    benchmarkTimeseries.status === 'error'
  ) {
    return <ErrorState />;
  }

  if (isEmptyData) {
    return <EmptyState />;
  }

  return (
    <PopulatedState
      benchmarkType={benchmarkType}
      benchmarkOption={benchmarkOption}
      setBenchmarkOption={handleBenchmarkOptionChange}
      period={period}
      setPeriod={setPeriod}
      portfolioTimeseries={portfolioTimeseries.data}
      benchmarkTimeseries={benchmarkTimeseries.data}
      startTimestamp={periodMap[period]}
      endTimestamp={periodMap.NOW}
    />
  );
};
